import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit2, Trash2, FlaskConical as Flask, Eye } from "lucide-react";
import { supabase } from "../lib/supabase";
import SEO from "./SEO";
import LabExercisePreviewModal from "./LabExercisePreviewModal";

export default function AdminLabExercises() {
  const [exercises, setExercises] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewExercise, setPreviewExercise] = useState<string | null>(null);

  useEffect(() => {
    fetchExercises();
  }, []);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("lab_exercises")
        .select("*")
        .order("order_number");

      if (error) throw error;
      setExercises(data || []);
    } catch (err) {
      console.error("Error fetching lab exercises:", err);
      setError(
        err instanceof Error ? err.message : "Failed to load lab exercises",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this lab exercise?")) return;

    try {
      const { error } = await supabase
        .from("lab_exercises")
        .delete()
        .eq("id", id);

      if (error) throw error;
      await fetchExercises();
    } catch (err) {
      console.error("Error deleting lab exercise:", err);
      alert("Failed to delete lab exercise");
    }
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from("lab_exercises")
        .update({ is_active: !isActive })
        .eq("id", id);

      if (error) throw error;
      await fetchExercises();
    } catch (err) {
      console.error("Error updating lab exercise:", err);
      alert("Failed to update lab exercise");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F98B3D]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="One80Labs - Manage Lab Exercises"
        description="Admin interface for managing lab exercises in the One80Labs content management system."
        noindex={true}
      />
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Lab Exercises</h1>
        <Link
          to="/admin/lab-exercises/new"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
        >
          <Plus className="h-5 w-5 mr-2" />
          New Lab Exercise
        </Link>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {exercises.map((exercise) => (
            <li key={exercise.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Flask className="h-5 w-5 text-[#F98B3D] mr-3" />
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {exercise.title}
                    </p>
                    <span
                      className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        exercise.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {exercise.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPreviewExercise(exercise.id)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <Link
                      to={`/admin/lab-exercises/${exercise.id}`}
                      className="text-[#F98B3D] hover:text-[#e07a2c]"
                    >
                      <Edit2 className="h-5 w-5" />
                    </Link>
                    <button
                      onClick={() => handleDelete(exercise.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="mt-2 sm:flex sm:justify-between">
                  <div className="sm:flex">
                    <p className="flex items-center text-sm text-gray-500">
                      Order: {exercise.order_number}
                    </p>
                  </div>
                  <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                    <button
                      onClick={() =>
                        toggleActive(exercise.id, exercise.is_active)
                      }
                      className={`text-xs font-medium px-2 py-1 rounded ${
                        exercise.is_active
                          ? "bg-red-50 text-red-700 hover:bg-red-100"
                          : "bg-green-50 text-green-700 hover:bg-green-100"
                      }`}
                    >
                      {exercise.is_active ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
          {exercises.length === 0 && (
            <li>
              <div className="px-4 py-12 text-center">
                <Flask className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No lab exercises found</p>
                <Link
                  to="/admin/lab-exercises/new"
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create your first lab exercise
                </Link>
              </div>
            </li>
          )}
        </ul>
      </div>

      <LabExercisePreviewModal
        isOpen={!!previewExercise}
        onClose={() => setPreviewExercise(null)}
        exerciseId={previewExercise || ""}
      />
    </div>
  );
}
